import type { AxiosResponse } from 'axios'
import type { RequestOptions, Result } from '/#/axios'
import { useMessage } from '/@/hooks/web/useMessage'
import { RequestEnum, ResultEnum } from '/@/enums/httpEnum'
import { isString } from '/@/utils/is'
import { setObjToUrlParams, toLogin } from '/@/utils'
import { useErrorLogStoreWithOut } from '/@/store/modules/errorLog'
import { useI18n } from '/@/hooks/web/useI18n'
import { joinTimestamp, formatRequestDate } from './helper'
import { refreshToken } from './refreshToken'
import axios from 'axios'

const { createMessage, createErrorModal } = useMessage()

/**
 * @description: 通用的响应数据处理
 */
export function createTransformRequestHook() {
  return (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { t } = useI18n()
    const { isTransformResponse, isReturnNativeResponse } = options
    // 错误的时候返回
    if (!res.data) {
      const error = new Error(t('sys.api.apiRequestFailed'))
      throw error
    }

    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data
    }

    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { code, data, msg } = res.data
    // 这里逻辑可以根据项目进行修改
    const hasSuccess = +code === ResultEnum.SUCCESS
    if (hasSuccess) {
      return data
    }

    let timeoutMsg = ''

    switch (code) {
      case ResultEnum.TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage')
        toLogin()
        break
      case ResultEnum.TOKEN_EXPIRED:
        timeoutMsg = msg
        toLogin()
        break
      default:
        timeoutMsg = msg || t('sys.api.apiRequestFailed')
    }

    if (options.errorMessageMode === 'modal') {
      createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg })
    } else if (options.errorMessageMode === 'message' && !options.ignoreBusinessError) {
      createMessage.error({
        content: timeoutMsg,
        key: 'timeout-message',
      })
    }

    return Promise.reject(res.data)
  }
}

/**
 * @description: 通用的请求前配置处理
 */
export function createBeforeRequestHook() {
  return (config, options) => {
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = false, urlPrefix } = options
    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`
    }

    if (apiUrl && isString(apiUrl) && !config.url?.startsWith('http')) {
      config.url = `${apiUrl}${config.url}`
    }
    const params = config.params || {}
    const data = config.data || false
    formatDate && data && !isString(data) && formatRequestDate(data)
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false))
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`
        config.params = undefined
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params)
        if (Reflect.has(config, 'data') && config.data && Object.keys(config.data).length > 0) {
          config.data = data
          config.params = params
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params
          config.params = undefined
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          )
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params
        config.params = undefined
      }
    }

    return config
  }
}

/**
 * @description: 通用的响应拦截器
 */
export function createResponseInterceptors() {
  return (res: AxiosResponse<any>) => {
    return res
  }
}

/**
 * @description: 通用的响应错误处理
 */
export function createResponseInterceptorsCatch(checkStatus: Function) {
  return async (error: any) => {
    const { t } = useI18n()
    const errorLogStore = useErrorLogStoreWithOut()
    errorLogStore.addAjaxErrorInfo(error)
    const { response, code, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
    const msg: string = response?.data?.error?.message ?? ''
    const err: string = error?.toString?.() ?? ''
    let errMessage = ''

    // 处理 401 错误 - Token 刷新逻辑
    if (response?.status === 401) {
      try {
        // 刷新 token
        const newToken = await refreshToken()

        // 更新原请求的 token
        if (config && config.headers) {
          const authenticationScheme = config.requestOptions?.authenticationScheme || ''
          config.headers.Authorization = authenticationScheme
            ? `${authenticationScheme} ${newToken}`
            : newToken
        }

        // 重新发起请求
        return axios.request(config)
      } catch (refreshError) {
        // 刷新 token 失败，执行原有的 401 处理逻辑
        checkStatus(401, msg, errorMessageMode)
        return Promise.reject(error)
      }
    }

    try {
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = t('sys.api.apiTimeoutMessage')
      }
      if (err?.includes('Network Error')) {
        errMessage = t('sys.api.networkExceptionMsg')
      }

      if (errMessage) {
        if (errorMessageMode === 'modal') {
          createErrorModal({ title: t('sys.api.errorTip'), content: errMessage })
        } else if (errorMessageMode === 'message') {
          createMessage.error({
            content: errMessage,
            key: 'err-message',
          })
        }
        return Promise.reject(error)
      }
    } catch (e: any) {
      throw new Error(e as unknown as string)
    }

    checkStatus(error?.response?.status, msg, errorMessageMode)

    return Promise.reject(error)
  }
}
